<script lang="ts" setup>
import {computed, ref, useId, watch} from 'vue';

export type ComboOption = {id: number; name: string};

const props = defineProps<{
  label: string;
  placeholder: string;
  allLabel: string;
  noResultsLabel: string;
  disabled: boolean;
  disabledHint: string;
  items: ComboOption[];
  loading: boolean;
  selectedId: number;
  selectedName: string;
}>();

const emit = defineEmits<{
  search: [query: string];
  select: [option: ComboOption];
  openChange: [open: boolean];
}>();

const query = ref('');
const open = ref(false);
const activeIndex = ref(-1);
const listboxId = useId();
const inputId = useId();

// The dropdown is absolutely positioned inside the collapsible filter panel, which has to clip
// its overflow to animate open/closed. Report the open state so the panel can stop clipping while
// a dropdown is showing - otherwise the result list is invisible and unclickable (see
// `devops-filter-panel-dropdown-open` in web_src/ee/css/dashboard.css).
watch(open, (value) => emit('openChange', value));

// The "all" pseudo-option (index 0) plus the search results, in on-screen order - the same
// order `<li v-for>` renders them in, so an index here always matches a real, visible option.
const allOptions = computed<ComboOption[]>(() => [{id: 0, name: props.allLabel}, ...props.items]);

function optionElementId(index: number): string {
  return `${listboxId}-option-${index}`;
}

const activeDescendant = computed(() => (open.value && activeIndex.value >= 0 ? optionElementId(activeIndex.value) : undefined));

// Shows the current selection (or the "all" label) while idle, a typing hint while
// the dropdown is open, and the disabled-state hint when the combo can't be used yet.
const placeholderText = computed(() => {
  if (props.disabled) return props.disabledHint;
  if (open.value) return props.placeholder;
  return props.selectedId ? props.selectedName : props.allLabel;
});

function onFocus() {
  if (props.disabled) return;
  open.value = true;
  activeIndex.value = -1;
  emit('search', query.value);
}

function onInput() {
  activeIndex.value = -1;
  emit('search', query.value);
}

function onBlur() {
  open.value = false;
  query.value = '';
  activeIndex.value = -1;
}

function choose(option: ComboOption) {
  query.value = '';
  open.value = false;
  activeIndex.value = -1;
  emit('select', option);
}

function onArrowDown() {
  if (props.disabled) return;
  if (!open.value) {
    open.value = true;
    emit('search', query.value);
  }
  const count = allOptions.value.length;
  if (!count) return;
  activeIndex.value = activeIndex.value < 0 ? 0 : (activeIndex.value + 1) % count;
}

function onArrowUp() {
  if (props.disabled) return;
  if (!open.value) {
    open.value = true;
    emit('search', query.value);
  }
  const count = allOptions.value.length;
  if (!count) return;
  activeIndex.value = activeIndex.value <= 0 ? count - 1 : activeIndex.value - 1;
}

function onEnter() {
  if (!open.value || activeIndex.value < 0) return;
  const option = allOptions.value[activeIndex.value];
  if (option) choose(option);
}

function onEscape() {
  open.value = false;
  activeIndex.value = -1;
}
</script>

<template>
  <div class="devops-filter-combo" :class="{'devops-filter-combo-disabled': disabled}">
    <label :for="inputId" class="devops-filter-label">{{ label }}</label>
    <div class="devops-filter-combo-input-wrap">
      <input
        :id="inputId"
        v-model="query"
        type="text"
        class="devops-filter-combo-input"
        :class="{'devops-filter-combo-input-selected': !open && selectedId > 0}"
        :placeholder="placeholderText"
        :disabled="disabled"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        :aria-expanded="open"
        :aria-controls="listboxId"
        :aria-activedescendant="activeDescendant"
        @focus="onFocus"
        @input="onInput"
        @blur="onBlur"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.esc="onEscape"
      >
      <ul v-if="open && !disabled" :id="listboxId" class="devops-filter-combo-list" role="listbox">
        <li
          :id="optionElementId(0)"
          role="option"
          :aria-selected="activeIndex === 0"
          :class="{'devops-filter-combo-option-active': activeIndex === 0}"
        >
          <button type="button" class="devops-filter-combo-option" @mousedown.prevent="choose({id: 0, name: ''})">
            {{ allLabel }}
          </button>
        </li>
        <li v-if="loading" class="devops-filter-combo-empty">…</li>
        <li v-else-if="!items.length" class="devops-filter-combo-empty">{{ noResultsLabel }}</li>
        <template v-else>
          <li
            v-for="(item, index) in items"
            :id="optionElementId(index + 1)"
            :key="item.id"
            role="option"
            :aria-selected="activeIndex === index + 1"
            :class="{'devops-filter-combo-option-active': activeIndex === index + 1}"
          >
            <button type="button" class="devops-filter-combo-option" @mousedown.prevent="choose(item)">
              {{ item.name }}
            </button>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>
